import React, { useState } from 'react';
import { Loader2, UploadCloud, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { saveMarketingSettings, migrateMarketingPublicApi, getMarketingPublicAudit } from '../../services/api';
import { WarningList } from './ContentApiStatus';

const ReportItem = ({ a }) => (
  <li className="border border-slate-200 rounded p-2" data-testid={`api-report-${a.slug}`}>
    <div className="text-xs font-medium text-slate-800">
      {a.title || a.slug}
      <span className="font-mono text-slate-500 ml-1">/{a.slug}</span>
      {a.ok === true && <span className="text-emerald-700 ml-2">migrated · v{a.contentVersion}</span>}
      {a.ok === false && <span className="text-red-700 ml-2" data-testid={`api-report-blocked-${a.slug}`}>blocked — not migrated until fixed</span>}
      {a.ok === undefined && <span className="text-slate-500 ml-2">{a.publicVersion ? `in API · v${a.publicVersion}` : a.status}{a.errors?.length > 0 ? ' · blocked' : ''}</span>}
    </div>
    {a.errors?.length > 0 && <div className="text-[11px] text-red-700 mt-1">{a.errors.join(' ')}</div>}
    {a.warnings?.length > 0 && <div className="mt-1.5"><WarningList warnings={a.warnings} /></div>}
  </li>
);

export const WebsiteApiSettings = ({ settings, onSaved }) => {
  const [form, setForm] = useState({
    websiteBaseUrl: settings?.websiteBaseUrl || 'https://www.lutrom.co.uk',
    websiteRevalidateUrl: settings?.websiteRevalidateUrl || '',
    websiteRevalidateSecret: '',
  });
  const [busy, setBusy] = useState(null);
  const [report, setReport] = useState(null);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) {
      toast.error(e?.response?.data?.detail || 'Request failed');
    } finally { setBusy(null); }
  };

  const save = () => run('save', async () => {
    const body = { ...form };
    if (!body.websiteRevalidateSecret) delete body.websiteRevalidateSecret;
    onSaved(await saveMarketingSettings(body));
    setForm((f) => ({ ...f, websiteRevalidateSecret: '' }));
    toast.success('Website settings saved');
  });

  const migrate = () => {
    if (!window.confirm('Copy every portal-published article that is not yet in the content API? Nothing is posted to LinkedIn.')) return;
    run('migrate', async () => {
      const r = await migrateMarketingPublicApi();
      setReport({ title: `Migrated ${r.migrated} · blocked ${r.blocked} (fix and re-run) · already in API ${r.alreadyInApi}`, items: r.report });
      toast.success(`Migration done — ${r.migrated} article(s) added to the content API`);
    });
  };

  const audit = () => run('audit', async () => {
    const r = await getMarketingPublicAudit();
    setReport({
      title: `${r.summary.total} articles · ${r.summary.inApi} in API · ${r.summary.withWarnings} with formatting warnings · ${r.summary.withErrors} blocked`,
      items: r.articles,
    });
  });

  return (
    <div className="space-y-3 border border-slate-200 rounded-md p-3" data-testid="website-api-settings">
      <div className="text-sm font-semibold text-slate-900">Website content API</div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Website base URL</Label>
        <Input value={form.websiteBaseUrl} onChange={(e) => setForm({ ...form, websiteBaseUrl: e.target.value })}
          data-testid="settings-website-base" />
        <div className="text-[11px] text-slate-500 mt-1">Used for canonical URLs and Verify live (/insights/&#123;slug&#125;).</div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Refresh webhook URL (optional)</Label>
        <Input value={form.websiteRevalidateUrl} placeholder="https://www.lutrom.co.uk/api/revalidate"
          onChange={(e) => setForm({ ...form, websiteRevalidateUrl: e.target.value })} data-testid="settings-webhook-url" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Webhook secret</Label>
        <Input type="password" autoComplete="off" value={form.websiteRevalidateSecret}
          placeholder={settings?.websiteRevalidateSecretSet ? '••••••••  (leave blank to keep existing)' : 'Shared secret for X-Lutrom-Signature'}
          onChange={(e) => setForm({ ...form, websiteRevalidateSecret: e.target.value })} data-testid="settings-webhook-secret" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={save} disabled={!!busy} data-testid="settings-website-save">
          {busy === 'save' && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />} Save website settings
        </Button>
        <Button size="sm" variant="outline" onClick={migrate} disabled={!!busy} data-testid="migrate-public-api-btn">
          {busy === 'migrate' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5 mr-1.5" />}
          Publish all live articles to API
        </Button>
        <Button size="sm" variant="outline" onClick={audit} disabled={!!busy} data-testid="audit-public-api-btn">
          {busy === 'audit' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ListChecks className="h-3.5 w-3.5 mr-1.5" />}
          Format audit
        </Button>
      </div>
      {report && (
        <div data-testid="public-api-report">
          <div className="text-xs font-medium text-slate-700 mb-2">{report.title}</div>
          <ul className="space-y-2 max-h-72 overflow-y-auto">
            {report.items.map((a) => <ReportItem key={a.id} a={a} />)}
          </ul>
        </div>
      )}
    </div>
  );
};
